import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Paper, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';
import { logToDatadog } from '../../../services/datadog';
import { BagLocation, Order, PointOfSale } from '@m4a3/api-client';
import { getShortIdForOrder, isOrderLate, formatOrderStatus } from '../../../utils/orderUtils';
import useInterval from '../../../utils/useInterval';
import {
    selectOverweightColorPairingsForOrder, 
} from '../../../selectors/ordersSelector';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux';
import { getOrder, resetOrder } from '../../../actions/orderActions';
import { STORAGE_LOCATIONS } from '../models/OrderStorageLocations';
import { newNotification } from '../../../actions/uiActions';
import { NOT_IN_PROGRESS_STATUSES, isExtraDeliveryOrder } from '../../../utils/orderUtils';
import { getDescriptionForCollectorStatus } from '../../Restaurant/types/CollectorStatus';

interface WeigherAppOrderCardDetailsProps {
  open: boolean;
  order: Order | null;
  orderIdWeightMap: {[key: string]: number};
  merchantLogoUrl?: string;
  onClose: () => void;
  onWeigh: () => void;
  onExtraOrder: (orderId: string) => void;
  pushToReceived: (order: Order) => void;
  showLocationStorageModalForOrder: (order: Order) => void;
}

const WeigherAppOrderCardDetails = ({
  open,
  order,
  orderIdWeightMap,
  merchantLogoUrl,
  onClose,
  onWeigh,
  onExtraOrder,
  pushToReceived,
  showLocationStorageModalForOrder
}: WeigherAppOrderCardDetailsProps) => {

    const dispatch = useAppDispatch();

    // =========================================
    // State Management
    // =========================================
    const [fullOrder, setFullOrder] = useState<Order | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [overDue, setOverDue] = useState(false);
    const [update, setUpdate] = useState(0);
    
    const overDueRef = useRef(false);

    const currentOrder: Order = fullOrder ?? order;
    
    // Use merchant data directly from order (provided by SSE)
    const merchantName = currentOrder?.outletName || "Unknown Merchant";

    const linkedOrdersForOverweightOrders = useAppSelector(state =>
      currentOrder ? selectOverweightColorPairingsForOrder(state, currentOrder.orderShortId) : null
    );
    
    const expectedDelivery = currentOrder ? new Date(currentOrder.expectedDeliveryDate).getTime() : 0;
    const currentTime = new Date().getTime();
    const THREE_MINUTES_FOR_DELIVERY = 180000;
    const TIME_FOR_TAKEOFF = expectedDelivery - THREE_MINUTES_FOR_DELIVERY;
    const timeDifference = TIME_FOR_TAKEOFF - currentTime;

    // =========================================
    // Lifecycle / Effect Hooks
    // =========================================
    useEffect(() => {
        // Load full order data when component mounts
        getOrderDetails();
    }, [order]);
 
    useEffect(() => {
        if (currentOrder) {
            setOverDue(isOrderLate(currentOrder));
            overDueRef.current = isOrderLate(currentOrder);
        }
    }, [currentOrder, currentOrder?.actualWeight, currentOrder?.status, currentOrder?.basket]);

    // Check overdue status every second
    useInterval(() => {
        if (currentOrder && isOrderLate(currentOrder) && overDueRef.current === false) {
            overDueRef.current = isOrderLate(currentOrder);
            setOverDue(true);
        }
    }, 1000);

    // Update time display every second
    useInterval(() => {
        setUpdate(update + 1);
    }, 1000);

    // =========================================
    // API Calls
    // =========================================
    const getOrderDetails = async () => {
        if (!order?.id) { return }
        
        try {
          let newOrder = await dispatch(getOrder(currentOrder?.id) as any);
          newOrder.bagLocations = order.bagLocations;
          
          setFullOrder(newOrder);
        } catch (err) {
          console.log(err);
        }
    };

    const handleReceived = async () => {''
        if (!currentOrder || !enableReceivedButton()) return;
        
        try {
            setIsLoading(true);
            pushToReceived(currentOrder);
        } catch (error) {
            logToDatadog('error_updating_order_status', { error, orderId: order.id }, 'error');
        } finally {
            setIsLoading(false);
        }
    };
    
    // =========================================
    // Data Helper Functions
    // =========================================
    // Get order ID in the format used in the app
    const orderNumber = getShortIdForOrder(currentOrder);
    
    const getOrderWeight = () => {
        if (orderIdWeightMap[currentOrder.id]) {
          return `${orderIdWeightMap[currentOrder.id]}kg`
        } else if (currentOrder.actualWeight) {
          return `${currentOrder.actualWeight}kg`
        } else {
          return null;
        }
    }
      
    const getLinkedOrdersText = () => {
        if (!linkedOrdersForOverweightOrders?.linkedOverweightOrderIds?.length) {
            return null;
        }
        
        const shortOrderIds = linkedOrdersForOverweightOrders.linkedOverweightOrderIds;
        return shortOrderIds.join(', ');
    };

    const enableReceivedButton = () => {
        return !['DISPATCHED', 
            'DELIVERED', 
            'RECEIVED_FROM_STORE', 
            'COMPLETE', 
            'REJECTED', 
            'CANCELLED', 
            'ABORTED', 
            'RESET',
            'READY'].includes(order.status)
    }
    
    // =========================================
    // Time Formatting Helper Functions
    // =========================================
    function getHours(diff: number) {
        return Math.floor(diff / 3600000);
    }

    function getMinutes(diff: number) {
        return Math.floor(diff / 60000);
    }

    function getSeconds(diff: number) {
        return Math.floor((diff / 1000) % 60);
    }

    function getDueTime(diff: number) {
        if (getMinutes(diff) > 60 * 48) {
        return 'in the future';
        } else if (getMinutes(diff) > 60 * 24 && getMinutes(diff) < 60 * 48) {
        return 'tomorrow';
        } else if (getMinutes(diff) > 60) {
        return `in ${Math.floor(diff / 60000 / 60)} hrs and ${Math.floor(
            (diff / 60000) % 60
        )} mins`;
        } else {
        return `in ${getMinutes(diff)} mins`;
        }
    }

    const numberToPaddedString = (num: number): string => {
        return `${num}`.padStart(2, '0');
    };

    // Calculate time display text
    const getTimeDisplayText = (): string => {
        const tenMins = 10;
        const isDueSoon = getMinutes(timeDifference) < tenMins;
        
        if (!overDue && !isDueSoon) {
        return `${getDueTime(timeDifference)}`;
        }

        if (!overDue && isDueSoon) {
        return `${numberToPaddedString(
            getMinutes(timeDifference)
        )}:${numberToPaddedString(getSeconds(timeDifference))} min`;
        }

        if (overDue) {
        if (getMinutes(-timeDifference) >= 60) {
            return `${getHours(-timeDifference)}:${numberToPaddedString(
            getMinutes(-timeDifference) % 60
            )}:${numberToPaddedString(getSeconds(-timeDifference))}`;
        }
        return `${numberToPaddedString(
            getMinutes(-timeDifference)
        )}:${numberToPaddedString(getSeconds(-timeDifference))}`;
        }
        
        return "Unknown"; // Fallback
    };

    // =========================================
    // UI/Styling Helper Functions
    // =========================================
    // Get text and background colors based on status
    function getTextColor() {
        if (overDue) {
        return '#ED0505';
        }
        return '#008A00';
    }

    // =========================================
    // UI Event Handlers
    // =========================================
    const didTapCreateExtraDelivery = () => {
        onExtraOrder(currentOrder.id);
        onClose();
    }

    const handleStorageLocationIconClick = () => {
        if (currentOrder) {
            showLocationStorageModalForOrder(currentOrder);
        }
    }

    // =========================================
    // Location Icons Helper
    // =========================================
    const renderLocationIcons = () => {
        if (!currentOrder?.bagLocations?.length) return null;
        
        // Create an array to hold all location icons, including duplicates
        const locationIconsToRender = [];
        
        // Parse all locations from the bagLocations array
        const allLocations = currentOrder.bagLocations
            .flatMap(locString => locString.split(','))
            .map(loc => loc.trim());
        
        // For each location in STORAGE_LOCATIONS
        STORAGE_LOCATIONS.forEach(location => {
            // Count how many times this location appears in the parsed locations
            const locationCount = allLocations.filter(
                loc => loc.toLowerCase() === location.id.toLowerCase()
            ).length;
            
            // Add this location to our array the appropriate number of times
            for (let i = 0; i < locationCount; i++) {
                locationIconsToRender.push(
                    <Box
                        key={`${location.id}-${i}`}
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            border: '1px solid #E7E7E7',
                            backgroundColor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            }
                        }}
                    >
                        <Typography 
                            sx={{ 
                                fontSize: '24px',
                            }}
                        >
                            {location.emoji}
                        </Typography>
                    </Box>
                );
            }
        });
        
        return locationIconsToRender;
    };

    /**
     * 
     * THis is purely a UX solution.
     * 
     * This button appears to unlink and remove the order, however it just resets the order. When linking orders
     * we ignore reset orders, so this will appear to also unlink the order with existing "in progress" orders. 
     * 
    */
    const handleRemoveExtraDelivery = async () => {

        if (!order) {
            dispatch(newNotification({ variant: 'error', message: 'Failed to remove extra delivery - Try to reset the order instead' }));
        }

        dispatch(resetOrder(currentOrder));
    };

    const showRemoveExtraDeliveryButton = () => {

        if (currentOrder && 
            isExtraDeliveryOrder(currentOrder) &&
            !NOT_IN_PROGRESS_STATUSES.includes(currentOrder.status)) {
            return true;
        }

        return false;
    }

    if (!open || !currentOrder) return null;
    
    // =========================================
    // Component Render
    // =========================================
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
            }}
            onClick={onClose}
        >
            <Paper
                elevation={3}
                sx={{
                    height: '65%',
                    width: '90%',
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: 3,
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Content area - will scroll if needed */}
                <Box 
                    sx={{ 
                        overflow: 'auto',
                        flexGrow: 1,
                        pb: 4, 
                    }}
                >
                    {/* Order ID and Time */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box 
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #E7E7E7',
                                borderRadius: '50px',
                                padding: '12px',
                                backgroundColor: 'white',
                                marginRight: '16px'
                            }}
                        >
                            {merchantLogoUrl && (
                                <Box 
                                    sx={{ 
                                        width: 32,
                                        height: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        backgroundColor: '#f5f5f5',
                                        mr: 1,
                                    }}
                                >
                                    <Box 
                                        component="img" 
                                        src={merchantLogoUrl} 
                                        alt="Store logo" 
                                        sx={{ 
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }} 
                                    />
                                </Box>
                            )}
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    fontSize: '36px',
                                }}
                            >
                                {orderNumber}
                            </Typography>
                        </Box>
                        
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                py: 0.5,
                                px: 2,
                                alignItems: 'center',
                                border: '1px solid #E7E7E7',
                                borderRadius: '50px',
                                backgroundColor: 'white',
                            }}
                        >
                            <Box 
                                component="span" 
                                sx={{ 
                                    width: '8px', 
                                    height: '8px', 
                                    borderRadius: '50%', 
                                    bgcolor: overDue ? '#ED0505' : '#4CAF50', 
                                    ml: 1,
                                    mr: 1 
                                }} 
                            />
                            <Typography 
                                variant="h6"
                                sx={{
                                    color: getTextColor(),
                                }}
                            >
                                {getTimeDisplayText()}
                            </Typography>
                        </Box>

                        {/* Storage Location Indicators */}
                        {currentOrder?.bagLocations?.length > 0 && (
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    ml: 2,
                                    gap: 1
                                }}
                                onClick={handleStorageLocationIconClick}
                            >
                                {renderLocationIcons()}
                            </Box>
                        )}

                        {/* Linked Orders UI Element */}
                        {getLinkedOrdersText() ? (
                            <Box 
                                sx={{ 
                                    backgroundColor: linkedOrdersForOverweightOrders?.color,
                                    border: '1px solid #E7E7E7',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    ml: 2
                                }}
                            >
                                <Typography
                                 sx={{ 
                                    color: 'white'
                                 }}
                                >
                                    Linked orders: {getLinkedOrdersText()}
                                </Typography>
                            </Box>
                        ): null}

                        <Box sx={{ marginLeft: getLinkedOrdersText() ? '0' : 'auto', marginRight: '24px' }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                Status
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {currentOrder.status || 'Received'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box 
                        sx={{ 
                            height: 'calc(100% - 175px - 24px)',
                            paddingBottom: '24px',
                            mb: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                        }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Order Details
                        </Typography>
                        <Typography variant="h5">
                            Order weight: {getOrderWeight()}
                        </Typography>
                        {currentOrder?.basket ? (
                            <>
                            <Typography variant="h5">
                                Order total: {currentOrder.basket.currency} {currentOrder.basket.totalCost}
                            </Typography>
                            {/* Display basket items in a scrollable container */}
                            {currentOrder.basket.items && currentOrder.basket.items.length > 0 && (
                                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Items:
                                </Typography>
                                <Box 
                                    sx={{ 
                                    flex: 1,
                                    overflowY: 'auto',
                                    pr: 1,
                                    borderRadius: '4px',
                                    p: 1
                                    }}
                                >
                                    {currentOrder.basket.items.map((item, index) => (
                                        <Box key={index} sx={{ mb: 1.5, pb: 1, borderBottom: index < currentOrder.basket.items.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                                            <Typography variant="h5">
                                                x{item.numberOfItems}: {item.name} {item.description} {currentOrder.basket.currency} {item.unitCost}
                                            </Typography>
                                            {item?.itemOptions && item.itemOptions.length > 0 ? (
                                                <Box sx={{ ml: 2, mt: 0.5 }}>
                                                    {item.itemOptions.map((itemOption, optionIndex) => (
                                                    <Typography 
                                                        key={`${index}-option-${optionIndex}`} 
                                                        variant="h5" 
                                                        sx={{ 
                                                        color: '#757575',
                                                        fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {itemOption.name} : {currentOrder.basket.currency} {itemOption.unitCost}
                                                    </Typography>
                                                    ))}
                                                </Box>
                                            ): null}
                                        </Box>
                                    ))}
                                </Box>
                                </Box>
                            )}
                            </>
                        ): (
                            <Typography variant="h5">
                                Could not retrieve basket
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Button area - fixed at the bottom with 24px padding */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        gap: 2,
                        justifyContent: 'space-between',
                        position: 'absolute',
                        bottom: '24px',
                        left: '24px',
                        right: '24px',
                    }}
                >
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleReceived}
                        disabled={!enableReceivedButton()}
                        sx={{
                            height: '175px',
                            py: 2,
                            backgroundColor: enableReceivedButton() ? '#4285F4': '#9E9E9E',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '40px',
                            lineHeight: '40px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: enableReceivedButton() ? '#2a75f3' : '#9E9E9E',
                            },
                        }}
                    >
                        Received {enableReceivedButton() ? '' : '✅'}
                    </Button>
                
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onWeigh}
                        disabled={isLoading}
                        sx={{
                            height: '175px',
                            py: 2,
                            backgroundColor: '#4285F4',
                            color: 'white',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '40px',
                            lineHeight: '40px',

                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#2a75f3',
                            },
                        }}
                    >
                        {currentOrder.actualWeight ? `New Weight` : `Weigh`}
                    </Button>
                    
                    {showRemoveExtraDeliveryButton() ? (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleRemoveExtraDelivery}
                            sx={{
                                height: '175px',
                                py: 2,
                                backgroundColor: '#4285F4',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '40px',
                                lineHeight: '40px',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: '#2a75f3',
                                },
                            }}
                        >
                            Remove extra delivery
                        </Button>
                    ):(
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={didTapCreateExtraDelivery}
                            disabled={isLoading || getLinkedOrdersText() !== null}
                            sx={{
                                height: '175px',
                                py: 2,
                                backgroundColor: '#4285F4',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '40px',
                                lineHeight: '40px',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: '#2a75f3',
                                },
                            }}
                        >
                            Extra Order
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default WeigherAppOrderCardDetails;